<template>
  <div class="main_right" @click="tip.show = false">
    <div class="Box" v-loading="generating" element-loading-text="AI正在生成阅读内容，请稍候..."
         element-loading-background="rgba(255,255,255,0.85)">
      <!-- ================= 步骤一：选词 + 高级设置 ================= -->
      <div v-if="step === 'config'" class="config-view">
        <div class="config-left">
          <div class="panel-title"><span>选择单词</span> <span class="source-label">--&nbsp;来源</span>
            <el-select v-model="source" size="small" placeholder="请选择集训" @change="onSourceChange" style="padding-left: 10px">
              <el-option v-for="item in options" :key="item.jxid" :label="item.jxmc" :value="item.jxid"/>
            </el-select>
          </div>
          <!-- 分类切换 -->
          <div class="cate-tabs">
            <div
                v-for="cate in wordCategories"
                :key="cate.value"
                class="cate-tab"
                :class="{ active: currentCate === cate.value }"
                @click="switchCate(cate.value)"
            >
              {{ cate.label }}
            </div>
          </div>
          <!-- 自动选词 -->
          <div class="auto-select" @click="autoSelect">
            <i class="el-icon-refresh"></i>
            <span>自动选词</span>
          </div>
          <!-- 单词墙 -->
          <div v-loading="wordLoading" class="word-wall">
            <span
                v-for="word in currentWords"
                :key="word.id"
                class="word-chip"
                :class="{ selected: selectedWords.includes(word.id) }"
                @click="toggleWord(word.id)"
            >
              {{ word.text }}
            </span>
            <div v-if="!wordLoading && !currentWords.length" class="word-empty">
              暂无{{ currentCate === 'wrong' ? '错词' : '掌握词' }}数据
            </div>
          </div>
          <div v-if="currentWords.length < pages.total" class="load-more" @click="loadMore">加载更多</div>
        </div>

        <!-- 高级设置 -->
        <div class="config-right">
          <div class="setting-header">高级设置</div>
          <div class="setting-body">
            <div class="setting-row">
              <label>文章数量</label>
              <div class="stepper">
                <span class="btn" @click="stepChange('articleCount', -1)">-</span>
                <span class="val">{{ form.articleCount }}</span>
                <span class="btn" @click="stepChange('articleCount', 1)">+</span>
              </div>
            </div>
            <div class="setting-row">
              <label>字数</label>
              <div class="stepper">
                <span class="btn" @click="stepChange('wordNum', -50)">-</span>
                <span class="val">{{ form.wordNum }}</span>
                <span class="btn" @click="stepChange('wordNum', 50)">+</span>
              </div>
            </div>
            <div class="setting-row">
              <label>题目数量</label>
              <div class="stepper">
                <span class="btn" @click="stepChange('questionNum', -1)">-</span>
                <span class="val">{{ form.questionNum }}</span>
                <span class="btn" @click="stepChange('questionNum', 1)">+</span>
              </div>
            </div>
            <div class="setting-group">
              <label>类型</label>
              <div class="tag-list">
                <span
                    v-for="t in typeOptions"
                    :key="t"
                    class="tag"
                    :class="{ active: form.type === t }"
                    @click="form.type = t"
                >{{ t }}</span>
              </div>
            </div>
            <div class="setting-group">
              <label>难度</label>
              <div class="tag-list">
                <span
                    v-for="d in difficultyOptions"
                    :key="d"
                    class="tag"
                    :class="{ active: form.difficulty === d }"
                    @click="form.difficulty = d"
                >{{ d }}</span>
              </div>
            </div>
          </div>
          <div class="confirm-btn" @click="generate">确定</div>
        </div>
      </div>

      <!-- ================= 步骤二：AI 阅读 ================= -->
      <div v-else class="reading-view">
        <!-- 顶部栏 -->
        <div class="reading-header">
          <div class="back" @click="backToConfig">
            <i class="el-icon-arrow-left"></i> 返回
          </div>
          <span class="title">AI阅读</span>
          <div class="read-aloud" :class="{ playing: speaking }" @click="toggleSpeak">
            <i v-if="ttsLoading" class="el-icon-loading"></i>
            <i v-else :class="speaking ? 'el-icon-video-pause' : 'el-icon-headset'"></i>
            {{ ttsLoading ? '合成中' : (speaking ? '停止朗读' : '点击朗读') }}
          </div>
          <audio ref="ttsAudio" style="display:none" @ended="onAudioEnded"></audio>
        </div>

        <div class="reading-scroll">
          <div class="reading-tip">
            <i class="el-icon-info"></i>
            点击单词翻译词义，双击句子翻译整句
          </div>

          <!-- 多篇文章切换 -->
          <div v-if="articles.length > 1" class="article-tabs">
            <span
                v-for="(a, ai) in articles"
                :key="ai"
                class="article-tab"
                :class="{ active: activeIndex === ai }"
                @click="switchArticle(ai)"
            >第{{ ai + 1 }}篇</span>
          </div>

          <!-- 文章 -->
          <div class="article-card">
            <h2 class="article-title">{{ article.title }}</h2>
            <div class="article-meta">
              <span class="cate">{{ article.category }}</span>
              <span class="count"><i class="el-icon-document"></i> {{ article.wordCount }}词</span>
            </div>
            <p class="article-body">
              <span
                  v-for="(sent, si) in renderedSentences"
                  :key="si"
                  class="sentence"
                  @dblclick.stop="showSentenceTip(sent.zh, $event)"
              ><template v-for="(tok, ti) in sent.tokens"><span
                  v-if="tok.trans"
                  :key="ti"
                  class="kw"
                  @click.stop="showTip(tok.text + ' — ' + tok.trans, $event)"
              >{{ tok.text }}</span><span v-else :key="ti">{{ tok.text }}</span></template> </span>
            </p>
          </div>

          <!-- 习题区 -->
          <div class="quiz-card">
            <div class="quiz-head">
              <span class="quiz-title">课后习题</span>
              <span v-if="submitted" class="quiz-score">
                得分：<b>{{ correctCount }}</b> / {{ article.questions.length }}
              </span>
            </div>

            <div
                v-for="(q, qi) in article.questions"
                :key="qi"
                class="quiz-item"
            >
              <p class="q-title">
                <span class="q-index">{{ qi + 1 }}.</span>{{ q.title }}
              </p>
              <div class="q-options">
                <div
                    v-for="opt in q.options"
                    :key="opt.key"
                    class="q-option"
                    :class="optionClass(q, opt)"
                    @click="chooseOption(qi, opt.key)"
                >
                  <span class="opt-key">{{ opt.key }}</span>
                  <span class="opt-text">{{ opt.text }}</span>
                  <i v-if="submitted && opt.key === q.answer" class="el-icon-success res-icon ok"></i>
                  <i v-else-if="submitted && q.chosen === opt.key && opt.key !== q.answer"
                     class="el-icon-error res-icon no"></i>
                </div>
              </div>
              <!-- 答案对比栏 -->
              <div v-if="submitted" class="answer-bar" :class="{ right: q.chosen === correctKey(q) }">
                <div class="answer-line">
                  <span>你的答案：<b>{{ q.chosen || '未作答' }}</b></span>
                  <span>正确答案：<b>{{ correctKey(q) }}</b></span>
                  <span class="judge">{{ q.chosen === correctKey(q) ? '回答正确' : '回答错误' }}</span>
                </div>
                <p v-if="q.analysis" class="answer-analysis">解析：{{ q.analysis }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="reading-footer">
          <div v-if="!submitted" class="foot-btn primary" @click="submitQuiz">提交答案</div>
          <template v-else>
            <div class="foot-btn plain" @click="resetQuiz">重新作答</div>
            <div class="foot-btn primary" @click="knowledgeDrawer = true">知识点</div>
          </template>
        </div>

        <!-- 翻译气泡 -->
        <div v-if="tip.show" class="trans-tip" :style="{ top: tip.y + 'px', left: tip.x + 'px' }">
          {{ tip.text }}
        </div>

        <!-- 知识点右侧抽屉 -->
        <el-drawer
            title="知识点解析"
            :visible.sync="knowledgeDrawer"
            direction="rtl"
            size="35%"
            custom-class="knowledge-drawer"
            append-to-body
        >
          <div class="k-toolbar">
            <el-button size="mini" type="primary" plain icon="el-icon-printer" @click="printKnowledge">打印</el-button>
          </div>
          <div id="knowledge-print" class="k-scroll">
            <h2 class="print-title">{{ article.title }} · 知识点解析</h2>
            <div v-for="(block, bi) in article.knowledge" :key="bi" class="k-block">
              <h3 class="k-title">{{ block.title }}</h3>
              <div v-for="(item, ii) in block.items" :key="ii" class="k-item">
                <p class="k-word"><span class="dot">·</span>{{ item.word }}</p>
                <p class="k-desc">{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </el-drawer>
      </div>
    </div>
  </div>
</template>

<script>
import {synthesize} from '@/utils/xfTts'
import {generateReading} from '@/utils/zhipuAI'
import {getTrains, getGroupStudentCount, getErrorWordList, getFamiliarWord} from '@/api/student'

export default {
  name: 'WordReading',
  data() {
    return {
      step: 'config', // config | reading
      // ---------- 选词 ----------
      currentCate: 'wrong', // wrong 错词 | mastered 掌握词
      wordCategories: [
        {label: '错词', value: 'wrong'},
        {label: '掌握词', value: 'mastered'}
      ],
      source: null,        // 当前集训 jxid
      options: [],         // 集训下拉列表
      currentData: {},     // 存放 bjid 等
      wordList: [],        // 当前分类加载的单词
      wordLoading: false,
      pages: {currentPage: 1, pageSize: 50, total: 0},
      selectedWords: [],
      maxWords: 10,        // 选词上限
      // ---------- 高级设置 ----------
      form: {
        articleCount: 1,
        wordNum: 200,
        questionNum: 3,
        type: '随机',
        difficulty: '小学'
      },
      typeOptions: ['随机', '故事', '科普', '社会文化', '新闻报道', '人物传记', '说明文', '议论文', '应用文', '记叙文', '文学'],
      difficultyOptions: ['小学', '初中', '高中', '大学', '托福', '雅思', 'GRE'],
      // ---------- AI 阅读 ----------
      articles: [],        // AI 生成的文章列表（可多篇）
      activeIndex: 0,      // 当前查看的文章下标
      generating: false,   // AI 生成中
      // ---------- 交互 ----------
      knowledgeDrawer: false,
      tip: {show: false, text: '', x: 0, y: 0},
      tipTimer: null,
      speaking: false,
      ttsLoading: false,
      ttsUrl: ''
    }
  },
  computed: {
    currentWords() {
      return this.wordList
    },
    // 当前查看的文章
    article() {
      return this.articles[this.activeIndex] || {
        title: '', category: '', wordCount: 0,
        sentences: [], keywords: [], questions: [], knowledge: [], submitted: false
      }
    },
    submitted() {
      return !!this.article.submitted
    },
    // 关键词映射：word(小写) -> trans
    keywordMap() {
      const map = {}
      ;(this.article.keywords || []).forEach(k => {
        if (k && k.word) map[k.word.toLowerCase()] = k.trans
      })
      return map
    },
    // 将每个句子拆分为 token，命中关键词的 token 带 trans
    renderedSentences() {
      const map = this.keywordMap
      return (this.article.sentences || []).map(sent => {
        const en = sent.en || ''
        // 按单词与非单词切分，保留标点/空格
        const parts = en.match(/[A-Za-z']+|[^A-Za-z']+/g) || []
        const tokens = parts.map(text => {
          const clean = text.toLowerCase().replace(/^'+|'+$/g, '')
          const trans = /[A-Za-z]/.test(text) ? map[clean] : undefined
          return {text, trans}
        })
        return {zh: sent.zh || '', tokens}
      })
    },
    correctCount() {
      return (this.article.questions || []).filter(q => q.chosen === this.correctKey(q)).length
    }
  },
  created() {
    this.initTrains()
  },
  methods: {
    // ---------- 集训 / 单词加载 ----------
    initTrains() {
      getTrains({yhid: this.$store.state.userInfo.yhid, yycdid: this.$store.state.yycdid}).then(res => {
        if (res.success) {
          if (res.result && res.result.length) {
            this.options = res.result
            this.source = this.options[0].jxid
            this.loadWords()
          } else {
            this.$message.warning('未参加集训，暂无数据')
          }
        } else {
          this.$message.error(res.resultDesc)
        }
      })
    },
    // 切换分类
    switchCate(value) {
      if (this.currentCate === value) return
      this.currentCate = value
      this.pages.currentPage = 1
      this.selectedWords = []
      this.loadWords()
    },
    // 切换集训
    onSourceChange() {
      this.pages.currentPage = 1
      this.selectedWords = []
      this.currentData = {}
      this.loadWords()
    },
    // 根据当前分类加载单词
    loadWords() {
      if (!this.source) return
      if (this.currentCate === 'wrong') {
        this.loadWrongWords()
      } else {
        this.loadMasteredWords()
      }
    },
    // 错词
    loadWrongWords() {
      const fetchList = () => {
        this.wordLoading = true
        getErrorWordList({
          bjid: this.currentData.bjid,
          lyid: this.source,
          xsid: this.$store.state.userInfo.yhid,
          currentPage: this.pages.currentPage,
          pageSize: this.pages.pageSize
        }).then(res => {
          this.wordLoading = false
          if (res.success) {
            this.wordList = (res.result.list || []).map(item => ({
              id: item.dcid || item.id,
              text: item.dcmc
            }))
            this.pages.total = res.result.total || 0
          } else {
            this.$message.error(res.resultDesc)
          }
        }).catch(() => {
          this.wordLoading = false
        })
      }
      // 错词列表需要 bjid，先取所在班级
      if (this.currentData.bjid) {
        fetchList()
      } else {
        getGroupStudentCount({xsid: this.$store.state.userInfo.yhid, jxid: this.source}).then(res => {
          if (res.success) {
            if (!res.result.bjid) {
              this.$message.warning('未查到所在班级，暂无错词数据')
              this.wordList = []
              return
            }
            this.currentData.bjid = res.result.bjid
            fetchList()
          } else {
            this.$message.error(res.resultDesc)
          }
        })
      }
    },
    // 掌握词（熟词）
    loadMasteredWords() {
      this.wordLoading = true
      getFamiliarWord({
        jxid: this.source,
        xsid: this.$store.state.userInfo.yhid,
        currentPage: this.pages.currentPage,
        pageSize: this.pages.pageSize
      }).then(res => {
        this.wordLoading = false
        const result = res.result || {}
        if (res.resultCode === 200 || res.success) {
          this.wordList = (result.list || []).map(item => ({
            id: item.dcid || item.id,
            text: item.dcmc
          }))
          this.pages.total = result.total || 0
        } else {
          this.$message.error(res.resultDesc)
        }
      }).catch(() => {
        this.wordLoading = false
      })
    },
    // ---------- 选词 ----------
    toggleWord(id) {
      const idx = this.selectedWords.indexOf(id)
      if (idx > -1) {
        this.selectedWords.splice(idx, 1)
      } else {
        if (this.selectedWords.length >= this.maxWords) {
          this.$message({message: `最多只能选择 ${this.maxWords} 个单词`, type: 'warning'})
          return
        }
        this.selectedWords.push(id)
      }
    },
    autoSelect() {
      const ids = this.currentWords.map(w => w.id)
      const count = Math.min(this.maxWords, ids.length)
      const shuffled = ids.slice().sort(() => Math.random() - 0.5)
      this.selectedWords = shuffled.slice(0, count)
    },
    loadMore() {
      if (this.wordList.length >= this.pages.total) {
        this.$message({message: '没有更多单词了', type: 'info'})
        return
      }
      this.pages.pageSize += 50
      this.loadWords()
    },
    stepChange(key, delta) {
      // 合理范围限制：文章固定 1 篇，字数 50-500 字，题目 1-3 题（减轻 AI 负担、加快生成）
      const limits = {
        articleCount: [1, 1],
        wordNum: [50, 500],
        questionNum: [1, 3]
      }
      const [min, max] = limits[key]
      let val = this.form[key] + delta
      if (val < min) {
        val = min
        this.$message({message: `不能小于 ${min}`, type: 'warning'})
      }
      if (val > max) {
        val = max
        this.$message({message: `不能大于 ${max}`, type: 'warning'})
      }
      this.form[key] = val
    },
    async generate() {
      if (!this.selectedWords.length) {
        this.$message({message: '请先选择单词', type: 'warning'})
        return
      }
      if (this.generating) return
      // 选中单词文本
      const words = this.currentWords
          .filter(w => this.selectedWords.includes(w.id))
          .map(w => w.text)
      this.generating = true
      this.stopSpeak()
      this.revokeTtsUrl()
      try {
        const tasks = []
        for (let i = 0; i < this.form.articleCount; i++) {
          tasks.push(generateReading({
            words,
            wordNum: this.form.wordNum,
            questionNum: this.form.questionNum,
            type: this.form.type,
            difficulty: this.form.difficulty
          }))
        }
        const results = await Promise.all(tasks)
        this.articles = results.map(a => ({...a, submitted: false}))
        this.activeIndex = 0
        this.step = 'reading'
      } catch (err) {
        console.error('AI生成阅读内容失败', err)
        this.$message({message: 'AI生成失败，请稍后重试', type: 'error'})
      } finally {
        this.generating = false
      }
    },
    switchArticle(index) {
      if (this.activeIndex === index) return
      this.stopSpeak()
      this.revokeTtsUrl()
      this.activeIndex = index
    },
    backToConfig() {
      this.stopSpeak()
      this.step = 'config'
    },
    // ---------- 朗读（科大讯飞 TTS）----------
    toggleSpeak() {
      if (this.speaking) {
        this.stopSpeak()
      } else {
        this.startSpeak()
      }
    },
    async startSpeak() {
      const audio = this.$refs.ttsAudio
      // 若已合成过同一篇文章，直接复用已缓存的音频，避免重复请求
      if (this.ttsUrl) {
        this.speaking = true
        audio.currentTime = 0
        audio.play()
        return
      }
      const text = (this.article.sentences || []).map(s => s.en || '').join(' ')
      this.ttsLoading = true
      this.speaking = true
      try {
        const url = await synthesize(text, {vcn: 'x4_EnUs_Laura_education', speed: 45})
        this.ttsUrl = url
        audio.src = url
        await audio.play()
      } catch (err) {
        console.error('讯飞语音合成失败', err)
        this.$message({message: '语音合成失败，请稍后重试', type: 'error'})
        this.speaking = false
      } finally {
        this.ttsLoading = false
      }
    },
    stopSpeak() {
      const audio = this.$refs.ttsAudio
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
      this.speaking = false
    },
    onAudioEnded() {
      this.speaking = false
    },
    revokeTtsUrl() {
      if (this.ttsUrl) {
        URL.revokeObjectURL(this.ttsUrl)
        this.ttsUrl = ''
      }
    },
    // ---------- AI 阅读 ----------
    showSentenceTip(zh, e) {
      this.showTip(zh || '（暂无翻译）', e)
    },
    showTip(text, e) {
      clearTimeout(this.tipTimer)
      const box = this.$el.querySelector('.reading-view').getBoundingClientRect()
      this.tip = {
        show: true,
        text,
        x: Math.max(10, e.clientX - box.left - 60),
        y: e.clientY - box.top + 18
      }
      this.tipTimer = setTimeout(() => {
        this.tip.show = false
      }, 2800)
    },
    // ---------- 习题 ----------
    chooseOption(qi, key) {
      if (this.submitted) return
      this.article.questions[qi].chosen = key
    },
    correctKey(q) {
      return (q.answer || '').toUpperCase()
    },
    optionClass(q, opt) {
      if (!this.submitted) {
        return {chosen: q.chosen === opt.key}
      }
      if (opt.key === q.answer) return {correct: true}
      if (q.chosen === opt.key && opt.key !== q.answer) return {wrong: true}
      return {}
    },
    submitQuiz() {
      const unanswered = (this.article.questions || []).filter(q => !q.chosen).length
      if (unanswered) {
        this.$message({message: `还有 ${unanswered} 道题未作答`, type: 'warning'})
        return
      }
      this.$set(this.article, 'submitted', true)
    },
    resetQuiz() {
      this.$set(this.article, 'submitted', false)
      ;(this.article.questions || []).forEach(q => {
        q.chosen = ''
      })
    },
    // ---------- 打印 ----------
    printKnowledge() {
      const content = document.getElementById('knowledge-print').innerHTML
      const win = window.open('', '_blank')
      win.document.write(`
        <html>
          <head>
            <title>${this.article.title} - 知识点</title>
            <style>
              body { font-family: "Microsoft YaHei", sans-serif; color: #333; padding: 30px; line-height: 1.8; }
              .print-title { text-align: center; font-size: 22px; margin-bottom: 24px; }
              .k-title { font-size: 18px; color: #1890FF; margin: 20px 0 12px; }
              .k-word { font-size: 15px; color: #333; font-weight: 600; margin: 10px 0 4px; }
              .k-desc { font-size: 14px; color: #666; margin: 0 0 8px; }
              .dot { margin-right: 6px; color: #1890FF; }
            </style>
          </head>
          <body>${content}</body>
        </html>
      `)
      win.document.close()
      win.focus()
      setTimeout(() => {
        win.print();
        win.close()
      }, 300)
    }
  },
  beforeDestroy() {
    this.stopSpeak()
    this.revokeTtsUrl()
  }
}
</script>

<style lang="scss" scoped>
.main_right {
  width: calc(100% - 10.5vw);
  height: 100%;
  float: left;
  box-sizing: border-box;
  padding: 0 2%;
  position: relative;

  .Box {
    width: 100%;
    height: 100%;
    box-shadow: 0px 0px 16px 0px rgba(89, 144, 255, 0.3);
    border-radius: 10px;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
    background: #fff;
  }
}

/* ============ 步骤一：选词 + 高级设置 ============ */
.config-view {
  width: 100%;
  height: 100%;
  display: flex;
  color: #333;
}

.config-left {
  flex: 1;
  min-width: 0;
  padding: 24px 24px 0;
  display: flex;
  flex-direction: column;
}

.panel-title {
  font-size: 1.15vw;
  font-weight: 600;
  color: #333;
  margin-bottom: 18px;
  padding-left: 12px;
  border-left: 4px solid #5990FF;
  line-height: 1.2;
}

.source-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  .source-label {
    font-size: 0.85vw;
    color: #555;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .el-select {
    flex: 1;
  }
}

.cate-tabs {
  display: flex;
  background: #F1F8FF;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 16px;
}

.cate-tab {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-size: 0.9vw;
  border-radius: 8px;
  cursor: pointer;
  color: #666;
  transition: all 0.2s;
}

.cate-tab.active {
  background: #5990FF;
  color: #fff;
  box-shadow: 0 4px 12px rgba(89, 144, 255, 0.35);
}

.auto-select {
  color: #5990FF;
  font-size: 0.85vw;
  margin-bottom: 14px;
  cursor: pointer;

  i {
    margin-right: 4px;
  }
}

.word-wall {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 12px;
}

.word-chip {
  padding: 8px 18px;
  border-radius: 20px;
  background: #F1F8FF;
  color: #555;
  font-size: 0.85vw;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  border: 1px solid transparent;
}

.word-chip:hover {
  border-color: #5990FF;
}

.word-chip.selected {
  background: #5990FF;
  color: #fff;
  box-shadow: 0 4px 10px rgba(89, 144, 255, 0.4);
}

.word-empty {
  width: 100%;
  text-align: center;
  color: #999;
  font-size: 0.9vw;
  padding: 40px 0;
}

.load-more {
  text-align: center;
  color: #999;
  font-size: 0.85vw;
  padding: 14px 0;
  cursor: pointer;
}

.config-right {
  width: 30%;
  min-width: 280px;
  background: #FAFCFF;
  border-left: 1px solid #EAF1FB;
  display: flex;
  flex-direction: column;
  padding: 24px 22px 0;
}

.setting-header {
  font-size: 1vw;
  font-weight: 600;
  color: #333;
  margin-bottom: 18px;
  padding-left: 12px;
  border-left: 4px solid #5990FF;
  line-height: 1.2;
}

.setting-body {
  flex: 1;
  overflow-y: auto;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;

  label {
    font-size: 0.85vw;
    color: #555;
  }
}

.stepper {
  display: flex;
  align-items: center;
  gap: 8px;

  .btn {
    width: 30px;
    height: 30px;
    line-height: 28px;
    text-align: center;
    background: #EAF1FB;
    color: #5990FF;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    user-select: none;

    &:hover {
      background: #d8e6fb;
    }
  }

  .val {
    min-width: 60px;
    height: 30px;
    line-height: 30px;
    text-align: center;
    background: #fff;
    border: 1px solid #EAF1FB;
    border-radius: 8px;
    font-size: 0.85vw;
    color: #333;
  }
}

.setting-group {
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 0.85vw;
    color: #555;
    margin-bottom: 10px;
  }
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  padding: 6px 12px;
  border-radius: 8px;
  background: #F1F8FF;
  color: #666;
  font-size: 0.8vw;
  cursor: pointer;
  transition: all 0.2s;
}

.tag.active {
  background: #5990FF;
  color: #fff;
}

.confirm-btn {
  margin: 12px 0 22px;
  height: 46px;
  line-height: 46px;
  text-align: center;
  border-radius: 24px;
  background: #5990FF;
  color: #fff;
  font-size: 0.95vw;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(89, 144, 255, 0.4);
}

/* ============ 步骤二：AI 阅读 ============ */
.reading-view {
  width: 100%;
  height: 100%;
  color: #333;
  position: relative;
  display: flex;
  flex-direction: column;
}

.reading-header {
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-bottom: 1px solid #EAF1FB;

  .back {
    position: absolute;
    left: 20px;
    font-size: 0.85vw;
    color: #5990FF;
    cursor: pointer;
  }

  .title {
    font-size: 1vw;
    font-weight: 600;
    color: #333;
  }

  .read-aloud {
    position: absolute;
    right: 20px;
    font-size: 0.8vw;
    color: #5990FF;
    background: #F1F8FF;
    padding: 6px 14px;
    border-radius: 16px;
    cursor: pointer;

    &.playing {
      background: #5990FF;
      color: #fff;
    }
  }
}

.reading-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 18px 26px 20px;
}

.reading-tip {
  padding: 10px 14px;
  background: #F1F8FF;
  border-radius: 10px;
  font-size: 0.8vw;
  color: #5990FF;
  margin-bottom: 18px;

  i {
    margin-right: 6px;
  }
}

.article-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.article-tab {
  padding: 6px 16px;
  border-radius: 16px;
  background: #F1F8FF;
  color: #5990FF;
  font-size: 0.8vw;
  cursor: pointer;
  transition: all 0.2s;

  &.active {
    background: #5990FF;
    color: #fff;
    box-shadow: 0 4px 10px rgba(89, 144, 255, 0.35);
  }
}

.article-card {
  background: #fff;
  border: 1px solid #EAF1FB;
  border-radius: 12px;
  padding: 22px 26px;
  box-shadow: 0 2px 12px rgba(89, 144, 255, 0.08);
  margin-bottom: 20px;
}

.sentence {
  cursor: pointer;
}

.article-title {
  text-align: center;
  font-size: 1.05vw;
  font-weight: 600;
  color: #333;
  margin-bottom: 14px;
}

.article-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .cate {
    background: #F1F8FF;
    color: #5990FF;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 0.78vw;
  }

  .count {
    font-size: 0.78vw;
    color: #999;
  }
}

.article-body {
  font-size: 0.95vw;
  line-height: 2.1;
  color: #333;

  .kw {
    color: #5990FF;
    font-weight: 600;
    cursor: pointer;
    padding: 0 2px;
    border-bottom: 1px dashed #5990FF;

    &:hover {
      background: #F1F8FF;
    }
  }

  .normal {
    cursor: text;
  }
}

.quiz-card {
  background: #fff;
  border: 1px solid #EAF1FB;
  border-radius: 12px;
  padding: 22px 26px;
  box-shadow: 0 2px 12px rgba(89, 144, 255, 0.08);
}

.quiz-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;

  .quiz-title {
    font-size: 1vw;
    font-weight: 600;
    color: #333;
    padding-left: 10px;
    border-left: 4px solid #5990FF;
    line-height: 1.2;
  }

  .quiz-score {
    font-size: 0.85vw;
    color: #666;

    b {
      color: #5990FF;
      font-size: 1vw;
    }
  }
}

.quiz-item {
  margin-bottom: 22px;

  &:last-child {
    margin-bottom: 0;
  }
}

.q-title {
  font-size: 0.92vw;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.6;

  .q-index {
    color: #5990FF;
    margin-right: 6px;
  }
}

.q-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.q-option {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border: 1px solid #EAF1FB;
  border-radius: 10px;
  font-size: 0.88vw;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: #5990FF;
  }

  .opt-key {
    width: 24px;
    height: 24px;
    line-height: 24px;
    text-align: center;
    border-radius: 50%;
    background: #F1F8FF;
    color: #5990FF;
    font-size: 0.8vw;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .opt-text {
    flex: 1;
  }

  .res-icon {
    font-size: 18px;
    margin-left: 8px;
  }

  .res-icon.ok {
    color: #8BDF1F;
  }

  .res-icon.no {
    color: #FF4545;
  }
}

.q-option.chosen {
  border-color: #5990FF;
  background: #F1F8FF;

  .opt-key {
    background: #5990FF;
    color: #fff;
  }
}

.q-option.correct {
  border-color: #8BDF1F;
  background: rgba(139, 223, 31, 0.08);

  .opt-key {
    background: #8BDF1F;
    color: #fff;
  }
}

.q-option.wrong {
  border-color: #FF4545;
  background: rgba(255, 69, 69, 0.06);

  .opt-key {
    background: #FF4545;
    color: #fff;
  }
}

.answer-bar {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #F1F8FF;
  font-size: 0.82vw;
  color: #666;

  .answer-line {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  b {
    color: #333;
  }

  .judge {
    margin-left: auto;
    color: #FF4545;
    font-weight: 600;
  }

  .answer-analysis {
    margin-top: 8px;
    line-height: 1.7;
    color: #666;
  }

  &.right {
    background: rgba(139, 223, 31, 0.1);

    .judge {
      color: #6FBF1A;
    }
  }
}

.reading-footer {
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  border-top: 1px solid #EAF1FB;

  .foot-btn {
    width: 26%;
    height: 46px;
    line-height: 46px;
    text-align: center;
    border-radius: 24px;
    font-size: 0.9vw;
    cursor: pointer;
  }

  .plain {
    background: #F1F8FF;
    color: #5990FF;
  }

  .primary {
    background: #5990FF;
    color: #fff;
    box-shadow: 0 6px 16px rgba(89, 144, 255, 0.4);
  }
}

.trans-tip {
  position: absolute;
  max-width: 260px;
  background: #333;
  color: #fff;
  font-size: 0.8vw;
  line-height: 1.6;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  z-index: 20;
  pointer-events: none;
}

/* ============ 知识点抽屉 ============ */
.k-toolbar {
  padding: 0 20px 12px;
  text-align: right;
  border-bottom: 1px solid #EAF1FB;
}

.k-scroll {
  padding: 16px 24px 24px;
  overflow-y: auto;
  height: calc(100% - 60px);
  box-sizing: border-box;
}

.print-title {
  display: none;
}

.k-block {
  margin-bottom: 24px;
}

.k-title {
  font-size: 0.98vw;
  font-weight: 600;
  color: #1890FF;
  margin-bottom: 12px;
}

.k-item {
  margin-bottom: 14px;
}

.k-word {
  font-size: 0.88vw;
  color: #333;
  font-weight: 600;
  margin-bottom: 4px;

  .dot {
    margin-right: 6px;
    color: #1890FF;
  }
}

.k-desc {
  font-size: 0.82vw;
  line-height: 1.7;
  color: #666;
}

/* ============ 手机端适配 ============ */
@media screen and (max-width: 768px) {
  .main_right {
    width: calc(100% - 90px);
    padding: 0 10px;
  }
  .config-view {
    flex-direction: column;
  }
  .config-left {
    padding: 16px 14px 0;
  }
  .config-right {
    width: 100%;
    min-width: 0;
    border-left: none;
    border-top: 1px solid #EAF1FB;
    padding: 16px 14px 0;
  }
  .panel-title, .setting-header, .quiz-head .quiz-title {
    font-size: 16px !important;
  }
  .cate-tab, .auto-select, .word-chip,
  .load-more, .setting-row label, .setting-group label,
  .tag, .stepper .val, .confirm-btn,
  .reading-header .title, .reading-header .read-aloud,
  .reading-header .back, .reading-tip, .article-title,
  .article-meta .cate, .article-meta .count,
  .foot-btn, .q-title, .q-option, .quiz-score,
  .answer-bar, .k-title, .k-word, .k-desc,
  .trans-tip, .q-option .opt-key {
    font-size: 13px !important;
  }
  .article-body {
    font-size: 15px !important;
  }
}

@media screen and (max-width: 480px) {
  .main_right {
    width: calc(100% - 70px);
    padding: 0 8px;
  }
  .answer-bar {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>

<style lang="scss">
.knowledge-drawer {
  .el-drawer__header {
    margin-bottom: 12px;
    padding: 16px 20px 0;
    color: #333;
    font-weight: 600;
    font-size: 18px;
  }

  .el-drawer__body {
    padding: 0;
    overflow: hidden;
  }
}

@media print {
  body * {
    visibility: hidden;
  }
  #knowledge-print, #knowledge-print * {
    visibility: visible;
  }
  #knowledge-print {
    position: absolute;
    left: 0;
    top: 0;
  }
  #knowledge-print .print-title {
    display: block !important;
  }
}
</style>
